/**
 * useAudioPlayback — decode + play a base64-encoded audio clip
 * (`TtsSynthesizeContent`'s shape) on the MAIN thread via WebAudio.
 *
 * Reimplements (cleanly, in TS, not copied) the gapless-playback idea
 * from `H:/dev/wayfinder/web/app.js`'s `speakSeq`/`primeSpeech` (~L1006):
 * one shared `AudioContext` for the hook instance's lifetime, and a
 * serial promise chain (`chainRef`) so back-to-back `play()` calls (e.g.
 * clicking 🔊 on a second message before the first finishes) schedule
 * gaplessly one after another on the SAME context instead of two
 * `AudioContext`s fighting over the audio output or overlapping into
 * garbled sound. Unlike wayfinder's route-fragment cache (fixed phrases
 * pre-synthesized once, replayed many times), there's no cache here —
 * every `play()` is a fresh clip from a fresh `synthesize()` call — so
 * this hook is just the scheduling half of that pattern, not the
 * priming half.
 *
 * `AudioContext`/`decodeAudioData` are Window-only APIs — this hook is
 * main-thread-only by construction (a React hook can't run in a
 * worker), so there's no Worker trap to guard against here the way
 * `ttsWorker.ts` has to.
 */
import { useCallback, useRef, useState } from 'react';
function base64ToArrayBuffer(b64) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}
export function useAudioPlayback() {
    const [playing, setPlaying] = useState(false);
    const [error, setError] = useState(null);
    const ctxRef = useRef(null);
    const sourceRef = useRef(null);
    const chainRef = useRef(Promise.resolve());
    // Bumped by `stopAndClear()`. Each `play()` call captures the generation
    // it was queued under; a queued clip whose generation is stale by the
    // time its turn in `chainRef` comes up skips decoding/playing entirely —
    // that's what makes a mid-stream cancel actually flush the queue instead
    // of just silencing the current clip and letting the rest play out.
    const generationRef = useRef(0);
    const getCtx = useCallback(() => {
        if (!ctxRef.current) {
            const Ctor = window
                .AudioContext ??
                window.webkitAudioContext;
            ctxRef.current = new Ctor();
        }
        return ctxRef.current;
    }, []);
    const play = useCallback((content) => {
        const generation = generationRef.current;
        const next = chainRef.current.then(async () => {
            // Cancelled (via `stopAndClear`) before this clip's turn came up —
            // skip it entirely rather than decoding/playing a clip the user
            // already asked to stop.
            if (generation !== generationRef.current)
                return;
            try {
                setError(null);
                const ctx = getCtx();
                if (ctx.state === 'suspended')
                    await ctx.resume();
                console.debug(`[legion-speech] playback: decoding clip (${content.mimeType})…`);
                const arrayBuffer = base64ToArrayBuffer(content.audioBase64);
                const buffer = await ctx.decodeAudioData(arrayBuffer);
                if (generation !== generationRef.current)
                    return;
                console.debug(`[legion-speech] playback: playing ${buffer.duration.toFixed(2)}s clip`);
                setPlaying(true);
                await new Promise((resolve) => {
                    const source = ctx.createBufferSource();
                    source.buffer = buffer;
                    source.connect(ctx.destination);
                    sourceRef.current = source;
                    source.onended = () => resolve();
                    source.start(0);
                });
                console.debug('[legion-speech] playback: done');
            }
            catch (err) {
                console.error('[legion-speech] playback: FAILED', err);
                setError(err instanceof Error ? err.message : String(err));
            }
            finally {
                sourceRef.current = null;
                setPlaying(false);
            }
        });
        chainRef.current = next;
        return next;
    }, [getCtx]);
    const stop = useCallback(() => {
        // `AudioBufferSourceNode.stop()` fires `onended`, which resolves the
        // pending `play()` promise in the chain above — no separate
        // chain-reset needed; the next queued `play()` proceeds normally.
        sourceRef.current?.stop();
    }, []);
    const stopAndClear = useCallback(() => {
        // Bump the generation FIRST so every clip already chained (but not yet
        // started) sees itself as stale and skips when its turn comes up, then
        // stop whatever's actively playing right now.
        generationRef.current += 1;
        sourceRef.current?.stop();
    }, []);
    return { play, stop, stopAndClear, playing, error };
}
